import {
  LAB_DETAILS_PAGE,
  NON_CITY_PRODUCT_PATH,
  DISEASE_CATEGORIES_PAGES,
  HEALTH_CHECKUP_LISTING_PAGE,
  CARE_PACKAGES_PAGE,
  PRODUCT_TYPE_TEST_SLUG,
  PRODUCT_TYPE_PACKAGE_SLUG,
  SITE_URL,
  LEAD_PAGE,
  LEAD_PAGE_INITIAL,
  EDOS_PAGE,
} from './constants'
import { STATE_REGION_MAP } from '@/helper/DATA'
import { formattedAddressObj } from './interfaces'

export const formatDateString = (dateString: string): string => {
  const date = new Date(dateString)

  if (isNaN(date.getTime())) {
    throw new Error('Invalid date string')
  }

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

export const calculateAge = (dob: string) => {
  const birthDate = new Date(dob)
  const currentDate = new Date()

  let age = currentDate.getFullYear() - birthDate.getFullYear()
  const monthDifference = currentDate.getMonth() - birthDate.getMonth()

  // If the current date is before the user's birthday in this year, subtract 1 from the age
  if (
    monthDifference < 0 ||
    (monthDifference === 0 && currentDate.getDate() < birthDate.getDate())
  ) {
    age--
  }

  return age
}

export function isEmpty(obj: any) {
  let isEmpty = false
  const type = typeof obj
  isEmpty = isEmpty || !obj
  isEmpty = isEmpty || type === 'undefined' // if it is undefined
  isEmpty = isEmpty || obj === null // if it is null
  isEmpty = isEmpty || (type === 'string' && obj.trim() === '') // if the string is empty or only have spaces
  isEmpty = isEmpty || obj === false || obj === 0 // if boolean value returns false
  isEmpty = isEmpty || (Array.isArray(obj) && obj.length === 0) // if array is empty
  isEmpty = isEmpty || (type === 'object' && Object.keys(obj).length === 0) // if object is empty
  return isEmpty
}

const BLANK_PLACEHOLDERS = new Set(["'", '-', '--', 'na', 'n/a', 'null'])

export function isNonBlank(value?: string | null): boolean {
  const trimmed = value?.trim() ?? ''
  return trimmed.length > 0 && !BLANK_PLACEHOLDERS.has(trimmed.toLowerCase())
}

export const deepClone = (data: any): any => {
  if (data instanceof File) {
    return data // Preserve File objects
  }

  if (Array.isArray(data)) {
    return data.map((item) => deepClone(item))
  }

  if (typeof data === 'object' && data !== null) {
    const copy: Record<string, any> = {}
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        copy[key] = deepClone(data[key])
      }
    }
    return copy
  }

  return data // Return primitive values as is
}

export const capitalize = (label?: string) => {
  if (label == null) return ''

  return label
    .split(/(\s*\/\s*|\s+)/)
    .map((part) =>
      part.trim() === '/' || part.trim() === '' || part.includes('/')
        ? part
        : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
    )
    .join('')
}

export const getDatesArray = (numOfDays: number) => {
  if (!numOfDays) return
  const dateArray = []
  const today = new Date()

  for (let i = 0; i < numOfDays; i++) {
    const currentDate = new Date(today)
    currentDate.setDate(today.getDate() + i)
    const day =
      currentDate?.getDate() === today?.getDate()
        ? 'Today'
        : currentDate.toLocaleString('en-us', { weekday: 'short' })
    const date = currentDate.getDate().toString().padStart(2, '0')
    const month = currentDate.toLocaleString('en-us', { month: 'short' })
    const fullDate = currentDate.toISOString().split('T')[0]
    dateArray.push({ day, date, month, fullDate })
  }

  return dateArray
}
export const formatDateAndTime = (dateString: string) => {
  if (!dateString) return

  const date = new Date(dateString)

  const day = date.getDate()
  const monthNum = (date.getMonth() + 1).toString().padStart(2, '0') // 01 for January
  const monthShort = date.toLocaleString('en-US', { month: 'short' }) // Jan, Feb, etc.
  const monthLong = date.toLocaleString('en-US', { month: 'long' }) // January, February, etc.
  const year = date.getFullYear()

  const weekday = date.toLocaleString('en-US', { weekday: 'long' }) // Monday, Tuesday, etc.

  let hours = date.getHours()
  const minutes = date.getMinutes()
  const period = hours >= 12 ? 'PM' : 'AM'

  hours = hours % 12
  if (hours === 0) {
    hours = 12
  }

  const formattedMinutes = minutes.toString().padStart(2, '0')

  // Full date in YYYY-MM-DD format
  const fullDate = `${year}-${monthNum}-${day.toString().padStart(2, '0')}`

  return {
    date: day,
    monthNum: monthNum,
    monthShort: monthShort,
    monthLong: monthLong,
    year: year,
    time: `${hours}:${formattedMinutes} ${period}`,
    period: period,
    weekday: weekday,
    fullDate: fullDate,
  }
}

export const customRound = (num: number) => {
  if (num % 1 === 0.5) {
    return Math.floor(num)
  } else {
    return Math.round(num)
  }
}

export const roundOff = (amount: number = 0) => {
  amount = +amount.toFixed(0)
  return amount
}

export const pageMetaTitle = (pageTitle: string) => {
  return `${pageTitle} | DDRC Agilus Diagnostics`
}

export const getProductTypeSlugByType = (type: string) =>
  type === 'PACKAGE' ? PRODUCT_TYPE_PACKAGE_SLUG : PRODUCT_TYPE_TEST_SLUG

export const getSlugByName = (name: string) => {
  if (!name) return ''
  return name
    .toLowerCase()
    .replace(/[,+,/]/g, '-')
    .replace(/\s+/g, '-')
    .replace(/[^\w-]/g, '')
    .replace(/-+/g, '-')
}

// DMS-managed slug when set; otherwise derived from the product name.
export const getProductSlug = (slugUrl?: string | null, name?: string | null) =>
  getSlugByName((slugUrl ?? '').trim()) || getSlugByName(name ?? '')

export const getProductUrl = (pDto: any, citySlug: string) => {
  if (isEmpty(pDto)) return '/'
  const pType = getProductTypeSlugByType(pDto?.type)
  const pSlug = getSlugByName(pDto?.name)
  return `/${pType}/${citySlug}/${pDto?.id}/${pSlug}`
}

export const getNonCityProductUrl = (pDto: any) => {
  if (isEmpty(pDto)) return '/'
  const pType = getProductTypeSlugByType(pDto?.type)
  const pSlug = getSlugByName(pDto?.name)
  return `/${pType}/${pDto?.id}/${pSlug}`
}

/**
 * Point an in-app product path at one of the detail page's sections.
 *
 * Returns '' when there is nothing real to link to: getProductUrl falls back to
 * '/' for a missing productDto, and '/#test-included' is a link to nowhere.
 */
export const getProductSectionUrl = (
  productUrl: string,
  sectionId: string
): string => {
  const [path] = (productUrl || '').split('#')
  if (!path || path === '/' || !sectionId) return ''
  return `${path}#${sectionId}`
}

export const getBannerDimension = (value: unknown, fallback: number): number =>
  Number(value) > 0 ? Number(value) / 2 : fallback

export const getBannerImageUrl = (item: any, isMobile: boolean): string => {
  const preferred = isMobile ? item?.mobileWebUrl : item?.desktopUrl
  const alternate = isMobile ? item?.desktopUrl : item?.mobileWebUrl
  if (typeof preferred === 'string' && preferred.trim()) return preferred
  if (typeof alternate === 'string' && alternate.trim()) return alternate
  return ''
}

/** True when a banner has at least one usable image URL, either variant. */
export const hasBannerImage = (item: any): boolean =>
  !!getBannerImageUrl(item, false)

const getSiteBaseUrl = (): string =>
  (process.env.NODE_ENV === 'development'
    ? 'http://localhost:3000'
    : SITE_URL
  ).replace(/\/$/, '')

const withLeadingSlash = (path: string): string =>
  path.startsWith('/') ? path : `/${path}`

export function getCanonicalUrl(path: string): string {
  const normalizedPath = withLeadingSlash(path)
  const canonicalPath =
    /\.\w+$/.test(normalizedPath) || normalizedPath.endsWith('/')
      ? normalizedPath
      : `${normalizedPath}/`
  return `${getSiteBaseUrl()}${canonicalPath}`
}

export function getSitemapUrl(path: string): string {
  const normalizedPath = withLeadingSlash(path)
  const sitemapPath =
    normalizedPath === '/' ? '' : normalizedPath.replace(/\/+$/, '')
  return `${getSiteBaseUrl()}${sitemapPath}`
}

// Forwards tracking params (e.g. ?srsltid=...) so they survive a canonical redirect.
export function preserveSearchParams(
  path: string,
  searchParams: Record<string, string | string[] | undefined>
): string {
  const qs = new URLSearchParams()
  Object.entries(searchParams).forEach(([key, value]) => {
    if (Array.isArray(value)) value.forEach((v) => qs.append(key, v))
    else if (value !== undefined) qs.append(key, value)
  })
  const queryString = qs.toString()
  return queryString ? `${path}?${queryString}` : path
}

export const getLabUrl = (itemDto: any, citySlug: string) => {
  if (isEmpty(itemDto)) return '/'
  let title = itemDto?.name
  if (itemDto?.areaName) title += ` ${itemDto?.areaName}`
  const labSlug = getSlugByName(title)
  const productUrl = `${LAB_DETAILS_PAGE}/${citySlug}/${itemDto?.clientId}/${labSlug}`
  return productUrl
}

export const isLabOpen = (workingFromTime: string, workingToTime: string) => {
  const now = new Date()
  const [fromHours, fromMinutes] = workingFromTime.split(':').map(Number)
  const [toHours, toMinutes] = workingToTime.split(':').map(Number)

  const currentTime = now.getHours() * 60 + now.getMinutes()
  const fromTime = fromHours * 60 + fromMinutes
  const toTime = toHours * 60 + toMinutes

  if (fromTime <= toTime) {
    return currentTime >= fromTime && currentTime <= toTime
  } else {
    return currentTime >= fromTime || currentTime <= toTime
  }
}

export const getAllPrimaryProfiles = (data: any) => {
  if (!Array.isArray(data)) return []
  return data.filter((item: any) => item?.primary === true)
}

export const formatAddress = (addressDto: formattedAddressObj): string => {
  const { houseNo, floor, landmark, mapAddress } = addressDto || {}
  return [houseNo, floor, landmark, mapAddress]
    ?.map((val) => val?.trim())
    ?.filter(Boolean)
    ?.join(', ')
}

/**
 * Groups opening hours by same timings
 * */
export const groupOpeningHours = (
  openingHoursDto: Array<{
    day: string
    openTime: string | null
    closeTime: string | null
  }>
) => {
  if (!openingHoursDto || openingHoursDto.length === 0) {
    return []
  }

  // Map to store time ranges and their corresponding days
  const timeRangeMap = new Map<string, string[]>()

  // Process each day's opening hours
  openingHoursDto.forEach((dayObj) => {
    const { day, openTime, closeTime } = dayObj

    // Skip closed days (when openTime or closeTime is null, undefined, or 'closed')
    if (
      !openTime ||
      !closeTime ||
      openTime === null ||
      closeTime === null ||
      openTime.toLowerCase() === 'closed' ||
      closeTime.toLowerCase() === 'closed'
    ) {
      return
    }

    // Create a unique key for this time range (times are already in 24-hour format)
    const timeRangeKey = `${openTime}-${closeTime}`

    // Group days with same time range
    if (timeRangeMap.has(timeRangeKey)) {
      timeRangeMap.get(timeRangeKey)?.push(day)
    } else {
      timeRangeMap.set(timeRangeKey, [day])
    }
  })

  const openingHoursSpec = Array.from(timeRangeMap.entries()).map(
    ([timeRange, days]) => {
      const [opens, closes] = timeRange.split('-')

      return {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: days.length === 1 ? days[0] : days,
        opens: opens.trim(),
        closes: closes.trim(),
      }
    }
  )

  return openingHoursSpec
}

export function getRegionCode(stateName: string | undefined): string {
  if (!stateName) return 'IN'
  const key = stateName?.trim()?.toLowerCase()
  return (STATE_REGION_MAP as Record<string, string>)[key] || 'IN'
}

// Without a city this is the non-city listing; interpolating an absent slug gave /disease/undefined/categories.
export const getDiseaseCategoriesUrl = (citySlug?: string): string =>
  citySlug
    ? `${DISEASE_CATEGORIES_PAGES}/${citySlug}/categories`
    : `${DISEASE_CATEGORIES_PAGES}/categories`

export const getDiseaseCategoriesProductListUrl = (
  citySlug?: string,
  diseaseName?: string
): string =>
  citySlug
    ? `${DISEASE_CATEGORIES_PAGES}/${citySlug}/disease-categories/${getSlugByName(
        diseaseName ?? ''
      )}`
    : `${DISEASE_CATEGORIES_PAGES}/disease-categories/${getSlugByName(
        diseaseName ?? ''
      )}`

export const getPopularHealthCheckupUrl = (citySlug?: string): string =>
  `${HEALTH_CHECKUP_LISTING_PAGE}/${citySlug}`

export const getCarePackagesUrl = (citySlug?: string): string =>
  `${CARE_PACKAGES_PAGE}/${citySlug}`

export const getCarePackageCategoryUrl = (
  citySlug?: string,
  categoryName?: string
): string =>
  `${CARE_PACKAGES_PAGE}/${citySlug}/packages/${getSlugByName(
    categoryName ?? ''
  )}`

export const getCarePackageNonCityCategoryUrl = (
  categoryName?: string
): string =>
  `${CARE_PACKAGES_PAGE}/packages/${getSlugByName(categoryName ?? '')}`

// Returns a sentence-cased name for a product or city slug, e.g. "thyroid-test" → "Thyroid test".
export const getSentenceCaseNameBySlug = (slug: string) => {
  if (!slug) return ''

  const name = slug.replace(/-/g, ' ')
  return name.charAt(0).toUpperCase() + name.slice(1)
}

// Returns a title-cased name for a product or city slug, e.g. "thyroid-test" → "Thyroid Test".
export const getTitleCaseNameBySlug = (slug?: string | null) =>
  capitalize(getSentenceCaseNameBySlug(slug ?? ''))

export const isNonCityProductRoute = (pathname: string): boolean => {
  // Guarded like isNonCityCarePackageRoute and isCityProductRoute below:
  // usePathname() is typed string but callers reach this on every TestCard
  // render, and an unguarded .startsWith on a nullish path throws.
  if (!pathname) return false
  // Strip internal rewrite prefix if present (usePathname() returns rewritten path)
  const cleanPath = pathname.startsWith(NON_CITY_PRODUCT_PATH)
    ? pathname.replace(NON_CITY_PRODUCT_PATH, '')
    : pathname
  const segments = cleanPath.split('/').filter(Boolean)
  return (
    segments.length === 3 &&
    [PRODUCT_TYPE_TEST_SLUG, PRODUCT_TYPE_PACKAGE_SLUG].includes(segments[0]) &&
    /^\d+$/.test(segments[1])
  )
}

export const isNonCityDiseaseCategoryRoute = (pathname: string): boolean => {
  const segments = pathname?.split('/').filter(Boolean) || []
  const diseaseSegment = DISEASE_CATEGORIES_PAGES.replace('/', '')
  if (segments[0] !== diseaseSegment) return false
  // /disease/categories (listing) and /disease/disease-categories/:slug (detail).
  return (
    (segments.length === 2 && segments[1] === 'categories') ||
    (segments.length === 3 && segments[1] === 'disease-categories')
  )
}

export const isNonCityCarePackageRoute = (pathname: string): boolean => {
  const segments = pathname?.split('/').filter(Boolean) || []
  const careSegment = CARE_PACKAGES_PAGE.replace('/', '')
  return (
    segments.length === 3 &&
    segments[0] === careSegment &&
    segments[1] === 'packages'
  )
}

export const isNonCityRoute = (pathname: string): boolean => {
  return (
    isNonCityProductRoute(pathname) ||
    isNonCityDiseaseCategoryRoute(pathname) ||
    isNonCityCarePackageRoute(pathname) ||
    isNonCityEdosRoute(pathname)
  )
}

// /edos and /edos/{pNo} have no city in the URL at all - treated as non-city, using the
// default city only for the API payload, same as other non-city routes below.
export const isNonCityEdosRoute = (pathname: string): boolean => {
  const segments = pathname?.split('/').filter(Boolean) || []
  if (segments[0] !== EDOS_PAGE.replace('/', '')) return false
  return segments.length === 1 || /^\d+$/.test(segments[1])
}

export const isCityProductRoute = (pathname: string): boolean => {
  const segments = pathname?.split('/').filter(Boolean)
  return (
    segments?.length === 4 &&
    ['test', 'package'].includes(segments?.[0]) &&
    /^\d+$/.test(segments?.[2])
  )
}

export interface FooterProductContext {
  productId: string
  citySlug?: string
}

// Single source of truth for "which product does the footer belong to", replacing the @footerExtras
// parallel route: mirroring PDP paths under a root slot crashed navigate-reducer whenever an
// intermediate level had no default.tsx. Add a PDP shape here instead of a new route mirror.
export const getFooterProductContext = (
  pathname: string
): FooterProductContext | null => {
  if (!pathname) return null

  const cleanPath = pathname.startsWith(NON_CITY_PRODUCT_PATH)
    ? pathname.replace(NON_CITY_PRODUCT_PATH, '')
    : pathname
  const segments = cleanPath.split('/').filter(Boolean)

  // /:pType/:city/:id/:pslug — id is the 3rd segment.
  if (isCityProductRoute(cleanPath)) {
    return { productId: segments[2], citySlug: segments[1] }
  }
  // /:pType/:id/:pslug — no city, so TestsInOtherCities falls back to DEFAULT_CITY_SLUG.
  if (isNonCityProductRoute(cleanPath)) {
    return { productId: segments[1] }
  }

  return null
}

// API names often already end in "Test"/"Package", so the label is not suffixed again.
const withTypeLabel = (productName: string, typeName: string) =>
  new RegExp(`\\b${typeName}$`, 'i').test(productName.trim())
    ? productName
    : `${productName} ${typeName}`

// Shared by the server and client halves of the footer's "in other cities" section so both render
// identical markup; a mismatch here would show up as a hydration error.
export const buildTestsInOtherCitiesItems = (
  cityPrices: any[],
  productId: string,
  serviceableCitySlugs: string[]
): { heading: string; items: { label: string; href: string }[] } | null => {
  if (!cityPrices?.length) return null

  const productName = cityPrices[0]?.name || ''
  const pSlug = getSlugByName(productName)
  const pTypeSlug = getProductTypeSlugByType(cityPrices[0]?.productType || '')
  if (!productName || !pSlug) return null

  const typeName = pTypeSlug === PRODUCT_TYPE_PACKAGE_SLUG ? 'Package' : 'Test'
  const labelledName = withTypeLabel(productName, typeName)

  // API returns all BE cities; empty slug list means "could not verify" so it must not drop every link.
  const seen = new Set<string>()
  const items = cityPrices
    .filter((row: any) => {
      const slug = row?.citySlugName
      if (!slug || seen.has(slug)) return false
      if (serviceableCitySlugs?.length && !serviceableCitySlugs.includes(slug))
        return false
      seen.add(slug)
      return true
    })
    .map((row: any) => ({
      label: `${labelledName} in ${getTitleCaseNameBySlug(row.citySlugName)}`,
      href: `/${pTypeSlug}/${row.citySlugName}/${
        row?.id ?? productId
      }/${pSlug}`,
    }))

  if (!items.length) return null

  return { heading: `${labelledName} in Other Cities`, items }
}

// Sub-routes sitting in the [city] slot; mistaking one for a city gives "Thyroid test in Categories". Keep in sync with src/app.
const RESERVED_CITY_SEGMENTS: Record<string, string[]> = {
  [DISEASE_CATEGORIES_PAGES.replace('/', '')]: [
    'categories',
    'disease-categories',
  ],
  [CARE_PACKAGES_PAGE.replace('/', '')]: ['packages'],
  [HEALTH_CHECKUP_LISTING_PAGE.replace('/', '')]: [],
  [LAB_DETAILS_PAGE.replace('/', '')]: [],
}

// City segment is index 0 on /health-checkup, /care-packages, /disease, /lab-details routes,
// and index 1 on /test|package/[city]/... (isCityProductRoute). Non-city routes return null.
export const getCitySlugFromPathname = (pathname: string): string | null => {
  if (isNonCityRoute(pathname)) return null
  const segments = pathname?.split('/').filter(Boolean) || []
  if (isCityProductRoute(pathname)) return segments[1] || null

  const reserved = RESERVED_CITY_SEGMENTS[segments[0]]
  if (!reserved) return null

  const citySlug = segments[1] || null
  return citySlug && reserved.includes(citySlug) ? null : citySlug
}

// getCitySlugFromPathname reads a URL segment positionally, so it happily returns a city that does
// not exist (/disease/bathinda/... yielded "… in Bathinda" links to pages that 404). Everything that
// turns a route slug into a label or a fetch must go through here instead.
export const getServiceableCitySlugFromPathname = (
  pathname: string,
  serviceableCitySlugs: string[]
): string | null => {
  const citySlug = getCitySlugFromPathname(pathname)
  return citySlug && serviceableCitySlugs?.includes(citySlug) ? citySlug : null
}

export const getLeadPageCitySlug = (pathname: string): string | null => {
  const segments = pathname?.split('/').filter(Boolean) || []
  if (segments.length !== 4) return null
  if (segments[0] !== LEAD_PAGE_INITIAL.replace('/', '')) return null
  if (segments[1] !== LEAD_PAGE.replace('/', '')) return null
  return segments[2]?.toLowerCase() || null
}

// Analytics SDKs are useless for crawlers and their CDNs serve bots inconsistently.
// Matches headless browsers too, so testing MoEngage in Playwright needs a real-browser UA override.
const BOT_USER_AGENT_PATTERN =
  /bot|crawl|spider|slurp|bingpreview|mediapartners-google|adsbot|lighthouse|headlesschrome|pagespeed|facebookexternalhit|embedly|preview/i

export const isBotUserAgent = (userAgent: string | null | undefined): boolean =>
  !!userAgent && BOT_USER_AGENT_PATTERN.test(userAgent)

// Lowest-priced entry of a TestCard-shaped list, for feeding PopularCityPricesSection a real product.
// Rows without a positive numeric price cannot be compared, so they are skipped.
export const getLowestPricedProductDto = (list?: any[]): any | null => {
  const priced = (list || [])
    .map((item: any) => item?.productDto)
    .filter((dto: any) => dto?.id && Number(dto?.price) > 0)
  if (!priced.length) return null
  return priced.reduce((lowest: any, dto: any) =>
    Number(dto.price) < Number(lowest.price) ? dto : lowest
  )
}
